import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FaYoutube, FaFacebookF, FaInstagram, FaBars, FaTimes, FaSun, FaMoon, FaGlobe } from 'react-icons/fa';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { DataProvider, useData } from './context/DataContext';
import { LanguageProvider, useLanguage } from './context/LanguageContext';
import LanguageSelector from './components/LanguageSelector';
import Home from './pages/Home';
import About from './pages/About';
import Events from './pages/Events';
import Contact from './pages/Contact';
import Videos from './pages/Videos';
import Admin from './pages/Admin';

const Main = styled.div`
  min-height: 100vh;
  width: 100%;
  max-width: 100vw;
  background: ${props => props.themeMode === 'light' 
    ? 'linear-gradient(135deg, #ffffff 0%, #f5f0ff 50%, #e8f4ff 100%)' 
    : 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)'};
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
  transition: all 0.4s ease;
`;

const NavWrapper = styled(motion.nav)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 3rem;
  background: ${props => props.themeMode === 'light'
    ? (props.scrolled ? 'rgba(255, 255, 255, 0.98)' : 'rgba(255, 255, 255, 0.9)')
    : (props.scrolled ? 'rgba(26, 26, 46, 0.95)' : 'rgba(26, 26, 46, 0.8)')};
  backdrop-filter: blur(10px);
  border-bottom: 1px solid ${props => props.themeMode === 'light' 
    ? 'rgba(124, 77, 255, 0.15)' 
    : 'rgba(179, 157, 219, 0.2)'};
  transition: all 0.3s ease;
  box-shadow: ${props => props.themeMode === 'light' 
    ? '0 2px 20px rgba(124, 77, 255, 0.1)' 
    : 'none'};
  
  @media (max-width: 1024px) {
    padding: 1rem 2rem;
  }
  
  @media (max-width: 768px) {
    padding: 0.8rem 1.5rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.8rem 1rem;
  }
`;

const Logo = styled(Link)`
  font-size: 1.5rem;
  font-weight: 800;
  text-decoration: none;
  background: ${props => props.themeMode === 'light'
    ? 'linear-gradient(90deg, #7c4dff 0%, #536dfe 50%, #7c4dff 100%)'
    : 'linear-gradient(90deg, #b39ddb 0%, #90caf9 100%)'};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  
  img {
    height: 45px;
    width: auto;
    object-fit: contain;
    transition: opacity 0.4s ease;
    
    @media (max-width: 768px) {
      height: 38px;
    }
    
    @media (max-width: 480px) {
      height: 32px;
    }
  }
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1rem;
  }
`;

const NavLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  @media (max-width: 1100px) {
    gap: 0.25rem;
  }
  
  @media (max-width: 900px) {
    display: ${props => props.isOpen ? 'flex' : 'none'};
    flex-direction: column;
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: ${props => props.themeMode === 'light'
      ? 'rgba(255, 255, 255, 0.98)'
      : 'rgba(26, 26, 46, 0.98)'};
    padding: 2rem;
    border-bottom: 1px solid ${props => props.themeMode === 'light'
      ? 'rgba(124, 77, 255, 0.15)'
      : 'rgba(179, 157, 219, 0.2)'};
    gap: 1.5rem;
  }
`;

const StyledLink = styled(Link)`
  color: ${props => props.active 
    ? '#7c4dff' 
    : (props.themeMode === 'light' ? '#5a5a7a' : '#b39ddb')};
  font-size: 0.9rem;
  font-weight: 600;
  text-decoration: none;
  padding: 0.5rem 0.75rem;
  border-radius: 8px;
  transition: all 0.3s ease;
  position: relative;
  white-space: nowrap;
  
  &:hover {
    color: #7c4dff;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: ${props => props.active ? '60%' : '0'};
    height: 2px;
    background: linear-gradient(90deg, #7c4dff, #536dfe);
    transition: width 0.3s ease;
  }
  
  &:hover::after {
    width: 60%;
  }
  
  @media (max-width: 1024px) {
    font-size: 0.85rem;
    padding: 0.5rem 0.5rem;
  }
  
  @media (max-width: 900px) {
    font-size: 1.1rem;
    padding: 0.8rem 1rem;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  margin-left: 1rem;
  padding-left: 1rem;
  border-left: 1px solid ${props => props.themeMode === 'light'
    ? 'rgba(124, 77, 255, 0.2)'
    : 'rgba(179, 157, 219, 0.3)'};
  
  @media (max-width: 900px) {
    margin-left: 0;
    padding-left: 0;
    border-left: none;
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 1px solid ${props => props.themeMode === 'light'
      ? 'rgba(124, 77, 255, 0.2)'
      : 'rgba(179, 157, 219, 0.3)'};
    gap: 1rem;
  }
`;

const SocialIcon = styled(motion.a)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${props => props.themeMode === 'light' 
    ? 'rgba(124, 77, 255, 0.1)' 
    : 'rgba(179, 157, 219, 0.1)'};
  color: ${props => props.themeMode === 'light' ? '#7c4dff' : '#b39ddb'};
  font-size: 1rem;
  transition: all 0.3s ease;
  
  &:hover {
    background: linear-gradient(135deg, #7c4dff 0%, #536dfe 100%);
    color: #fff;
    transform: scale(1.1);
  }
  
  @media (max-width: 900px) {
    width: 44px;
    height: 44px;
    font-size: 1.2rem;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: ${props => props.themeMode === 'light' ? '#5a5a7a' : '#b39ddb'};
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  
  @media (max-width: 900px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const ContentWrapper = styled.div`
  margin-top: 70px;
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;
  
  @media (max-width: 768px) {
    margin-top: 60px;
  }
`;

const Footer = styled.footer`
  background: ${props => props.themeMode === 'light'
    ? 'linear-gradient(135deg, #f5f0ff 0%, #e8f4ff 100%)'
    : 'rgba(0, 0, 0, 0.3)'};
  color: ${props => props.themeMode === 'light' ? '#5a5a7a' : '#a0a0c0'};
  text-align: center;
  padding: 3rem 2rem;
  margin-top: auto;
  border-top: 1px solid ${props => props.themeMode === 'light'
    ? 'rgba(124, 77, 255, 0.1)'
    : 'transparent'};
  
  @media (max-width: 768px) {
    padding: 2rem 1rem;
  }
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const FooterLinks = styled.div`
  display: flex;
  justify-content: center;
  gap: 2rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  
  @media (max-width: 600px) {
    gap: 1rem;
  }
`;

const FooterLink = styled(Link)`
  color: ${props => props.themeMode === 'light' ? '#5a5a7a' : '#b39ddb'};
  text-decoration: none;
  font-size: 0.95rem;
  transition: color 0.3s ease;
  
  &:hover {
    color: #7c4dff;
  }
`;

const FooterSocial = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const Copyright = styled.p`
  font-size: 0.9rem;
  color: ${props => props.themeMode === 'light' ? '#888' : '#666'};
  
  @media (max-width: 480px) {
    font-size: 0.8rem;
  }
`;

const LanguageSwitcher = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid ${props => props.themeMode === 'light'
    ? 'rgba(124, 77, 255, 0.1)'
    : 'rgba(255, 255, 255, 0.1)'};
`;

const LanguageButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  border: 1px solid ${props => props.active 
    ? '#7c4dff' 
    : (props.themeMode === 'light' ? 'rgba(124, 77, 255, 0.2)' : 'rgba(255, 255, 255, 0.1)')};
  background: ${props => props.active 
    ? 'rgba(124, 77, 255, 0.15)' 
    : 'transparent'};
  color: ${props => props.active 
    ? '#7c4dff' 
    : (props.themeMode === 'light' ? '#5a5a7a' : '#a0a0c0')};
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: #7c4dff;
    color: #7c4dff;
  }
`;

const LanguageLabel = styled.span`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: ${props => props.themeMode === 'light' ? '#888' : '#666'};
  font-size: 0.9rem;
`;

// Floating Theme Toggle Button
const ThemeToggle = styled(motion.button)`
  position: fixed;
  bottom: 30px;
  right: 30px;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  border: none;
  background: ${props => props.themeMode === 'light' 
    ? 'linear-gradient(135deg, #7c4dff 0%, #536dfe 100%)' 
    : 'linear-gradient(135deg, #ffd54f 0%, #ffb300 100%)'};
  color: #fff;
  font-size: 1.5rem;
  cursor: pointer;
  box-shadow: 0 8px 30px ${props => props.themeMode === 'light' 
    ? 'rgba(124, 77, 255, 0.4)' 
    : 'rgba(255, 213, 79, 0.4)'};
  z-index: 1001;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.1);
    box-shadow: 0 12px 40px ${props => props.themeMode === 'light' 
      ? 'rgba(124, 77, 255, 0.6)' 
      : 'rgba(255, 213, 79, 0.6)'};
  }
  
  @media (max-width: 768px) {
    bottom: 20px;
    right: 20px;
    width: 50px;
    height: 50px;
    font-size: 1.2rem;
  }
  
  @media (max-width: 480px) {
    bottom: 15px;
    right: 15px;
    width: 45px;
    height: 45px;
    font-size: 1.1rem;
  }
`;

// Navigation component with location awareness
function Navigation() {
  const location = useLocation();
  const { theme } = useTheme();
  const { t } = useLanguage();
  const { data } = useData();
  const [scrolled, setScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const navLinks = [
    { path: '/', label: t.nav.home },
    { path: '/about', label: t.nav.about },
    { path: '/events', label: t.nav.events },
    { path: '/videos', label: t.nav.videos },
    { path: '/contact', label: t.nav.contact }
  ];

  return (
    <NavWrapper
      scrolled={scrolled}
      themeMode={theme.mode}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Logo to="/" themeMode={theme.mode}>
        <img 
          src={theme.mode === 'dark' ? `/logoowhite.png?v=${Date.now()}` : `/logoo.png?v=${Date.now()}`} 
          alt="HOW Bangkal" 
        />
        House Of Worship Bangkal
      </Logo>
      
      <MobileMenuButton onClick={() => setIsOpen(!isOpen)} themeMode={theme.mode}>
        {isOpen ? <FaTimes /> : <FaBars />}
      </MobileMenuButton>

      <NavLinks isOpen={isOpen} themeMode={theme.mode}>
        {navLinks.map(link => (
          <StyledLink
            key={link.path}
            to={link.path}
            active={location.pathname === link.path ? 1 : 0}
            themeMode={theme.mode}
          >
            {link.label}
          </StyledLink>
        ))}
        <SocialLinks themeMode={theme.mode}>
          <SocialIcon
            href={data.socialLinks?.youtube || "https://youtube.com"}
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.15 }}
            whileTap={{ scale: 0.9 }}
            themeMode={theme.mode}
          >
            <FaYoutube />
          </SocialIcon>
          <SocialIcon
            href={data.socialLinks?.facebook || "https://facebook.com"}
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.15 }}
            whileTap={{ scale: 0.9 }}
            themeMode={theme.mode}
          >
            <FaFacebookF />
          </SocialIcon>
          <SocialIcon
            href={data.socialLinks?.instagram || "https://instagram.com"}
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.15 }}
            whileTap={{ scale: 0.9 }}
            themeMode={theme.mode}
          >
            <FaInstagram />
          </SocialIcon>
        </SocialLinks>
      </NavLinks>
    </NavWrapper>
  );
}

// Main App Content
function AppContent() {
  const { theme, isDarkMode, toggleTheme } = useTheme();
  const { t, language, setLanguage } = useLanguage();
  const { data } = useData();
  const location = useLocation();
  
  // Check if current route is admin
  const isAdminRoute = location.pathname === '/admin';

  // Don't show main layout for admin page
  if (isAdminRoute) {
    return (
      <Routes>
        <Route path="/admin" element={<Admin />} />
      </Routes>
    );
  }

  return (
    <Main themeMode={theme.mode}>
      <LanguageSelector />
      <Navigation />
      <ContentWrapper>
        <AnimatePresence mode="wait">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/events" element={<Events />} />
            <Route path="/videos" element={<Videos />} />
            <Route path="/contact" element={<Contact />} />
          </Routes>
        </AnimatePresence>
      </ContentWrapper>
      
      <Footer themeMode={theme.mode}>
        <FooterContent>
          <FooterLinks>
            <FooterLink to="/" themeMode={theme.mode}>{t.nav.home}</FooterLink>
            <FooterLink to="/about" themeMode={theme.mode}>{t.nav.about}</FooterLink>
            <FooterLink to="/events" themeMode={theme.mode}>{t.nav.events}</FooterLink>
            <FooterLink to="/videos" themeMode={theme.mode}>{t.nav.videos}</FooterLink>
            <FooterLink to="/contact" themeMode={theme.mode}>{t.nav.contact}</FooterLink>
          </FooterLinks>
          <FooterSocial>
            <SocialIcon
              href={data.socialLinks?.youtube || "https://youtube.com"}
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.15 }}
              themeMode={theme.mode}
            >
              <FaYoutube />
            </SocialIcon>
            <SocialIcon
              href={data.socialLinks?.facebook || "https://facebook.com"}
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.15 }}
              themeMode={theme.mode}
            >
              <FaFacebookF />
            </SocialIcon>
            <SocialIcon
              href={data.socialLinks?.instagram || "https://instagram.com"}
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.15 }}
              themeMode={theme.mode}
            >
              <FaInstagram />
            </SocialIcon>
          </FooterSocial>
          <Copyright themeMode={theme.mode}>
            © {new Date().getFullYear()} House Of Worship Bangkal. {t.home.footerRights}
          </Copyright>
          
          {/* Language Switcher in Footer */}
          <LanguageSwitcher themeMode={theme.mode}>
            <LanguageLabel themeMode={theme.mode}>
              <FaGlobe /> {t.home.language}:
            </LanguageLabel>
            <LanguageButton 
              themeMode={theme.mode}
              active={language === 'en'}
              onClick={() => setLanguage('en')}
            >
              🇺🇸 English
            </LanguageButton>
            <LanguageButton 
              themeMode={theme.mode}
              active={language === 'fil'}
              onClick={() => setLanguage('fil')}
            >
              🇵🇭 Filipino
            </LanguageButton>
          </LanguageSwitcher>
        </FooterContent>
      </Footer>

      {/* Floating Theme Toggle */}
      <ThemeToggle
        onClick={toggleTheme}
        themeMode={theme.mode}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        initial={{ opacity: 0, y: 100 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5, duration: 0.5 }}
        title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
      >
        {isDarkMode ? <FaSun /> : <FaMoon />}
      </ThemeToggle>
    </Main>
  );
}

function App() {
  return (
    <ThemeProvider>
      <LanguageProvider>
        <DataProvider>
          <Router>
            <AppContent />
          </Router>
        </DataProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
